import {
  FileText,
  MessageSquare,
  BarChart3,
  RefreshCw,
  Shield,
  Target,
  Lightbulb,
  Settings,
  Plus,
} from "lucide-react";

const executiveFeatures = [
  {
    name: "Chief Strategy Officer (CSO)",
    description: "Strategic planning, market analysis, and competitive positioning",
    icon: Target,
  },
  {
    name: "Chief Financial Officer (CFO)",
    description: "Financial planning, budgeting, forecasting, and investment strategies",
    icon: BarChart3,
  },
  {
    name: "Chief Technology Officer (CTO)",
    description: "Technology roadmaps, digital transformation, and innovation leadership",
    icon: Settings,
  },
  {
    name: "Chief Operations Officer (COO)",
    description: "Operations optimization, process improvement, and efficiency strategies",
    icon: RefreshCw,
  },
  {
    name: "Chief Product Officer (CPO)",
    description: "Product strategy, user experience, and product-market fit guidance",
    icon: Lightbulb,
  },
  {
    name: "More Coming Soon",
    description: "Additional executive AI personas are being developed to expand your C-suite team",
    icon: Plus,
    isComingSoon: true,
  },
];

const platformFeatures = [
  {
    name: "Document Generation",
    description: "Create professional strategic documents in minutes with AI assistance",
    icon: FileText,
  },
  {
    name: "Strategy Chat",
    description: "Get instant expert-level business strategy advice through our AI chat interface",
    icon: MessageSquare,
  },
  {
    name: "Secure Platform",
    description: "Enterprise-grade security for your sensitive business data",
    icon: Shield,
  },
];

const Features = () => {
  return (
    <div 
      id="features" 
      className="py-24 bg-white"
      tabIndex={-1}
      role="region"
      aria-label="Features section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Your Complete C-Suite AI Team
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Access specialized AI personas for every executive role, plus powerful tools to transform your business strategy
          </p>
        </div>

        {/* Executive Personas Section */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">
            Executive AI Personas
          </h3>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {executiveFeatures.map((feature, index) => (
              <div
                key={feature.name}
                className={`relative group p-6 rounded-2xl shadow-sm border transition-all duration-300 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-cso-600 ${
                  feature.isComingSoon 
                    ? 'bg-gray-50 border-gray-200' 
                    : 'bg-white border-gray-200 hover:shadow-md'
                }`}
                role="article"
                aria-labelledby={`executive-heading-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-shrink-0" aria-hidden="true">
                    <feature.icon className={`h-6 w-6 ${feature.isComingSoon ? 'text-gray-400' : 'text-cso-600'}`} />
                  </div>
                  <h4 
                    id={`executive-heading-${index}`}
                    className={`text-lg font-semibold ${feature.isComingSoon ? 'text-gray-500' : 'text-gray-900'}`}
                  >
                    {feature.name}
                  </h4>
                  {feature.isComingSoon && (
                    <span className="ml-auto px-2 py-1 text-xs font-medium text-gray-500 bg-gray-200 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className={`${feature.isComingSoon ? 'text-gray-500' : 'text-gray-600'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Features Section */}
        <div>
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">
            Platform Features
          </h3>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {platformFeatures.map((feature, index) => (
              <div
                key={feature.name}
                className="relative group bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-cso-600"
                role="article"
                aria-labelledby={`platform-heading-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-shrink-0" aria-hidden="true">
                    <feature.icon className="h-6 w-6 text-cso-600" />
                  </div>
                  <h4 
                    id={`platform-heading-${index}`}
                    className="text-lg font-semibold text-gray-900"
                  >
                    {feature.name}
                  </h4>
                </div>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
